import type {ReactElement} from 'react';
import {useState} from 'react';
import {ShareInTeamBarState} from '@Components/share-in-team-toggle-bar/share-in-team-toggle-bar.types';
import {ShareInTeamToggleBar} from '../share-in-team-toggle-bar/share-in-team-toggle-bar';
import {isUserPremium} from '@Libraries/user.library';
import {noop} from '@Utils/general.util';
import type {EmailSignupForm} from '@FormMakerReact/email-signup-form.types';
import {shareEmailSignupFormInTeam} from '@Libraries/email-signup-form-library';
import {openShareSignupFormInTeamModal} from '@Modals/share-signup-form-in-team-modal';
import {useIsMobileWidth} from '@Hooks/useIsMobileWidth';
import {useIsInModal} from '@Components/modal/hooks/useIsInModal';
import {CollaborationGA4Source, CollaborationGA4Toggle, GA4EventParamName} from '@Libraries/ga-events';

export interface ShareSignupFormInTeamTogglerProps {
  signupForm: EmailSignupForm;
  onStateUpdate?: (state: ShareInTeamBarState) => void;
  className?: string;
  gaEvent?: string;
  gaSource?: string;
}

export function ShareSignupFormInTeamToggler({signupForm, onStateUpdate = noop, className = '', gaEvent = '', gaSource = ''}: ShareSignupFormInTeamTogglerProps): ReactElement<any> {
  const isMobileWidth = useIsMobileWidth();
  const isInModal = useIsInModal();
  const getToggleStateBySignupFormState = (): ShareInTeamBarState => {
    if (!isUserPremium()) {
      return ShareInTeamBarState.UPSELLING;
    }

    if (signupForm.isSharedInTeam) {
      return ShareInTeamBarState.ACTIVE;
    }

    return ShareInTeamBarState.INACTIVE;
  };

  const [isLoading, setIsLoading] = useState(false);
  const [toggleState, setToggleState] = useState(getToggleStateBySignupFormState());

  const updateSharingStatus = async (shareInTeamState: ShareInTeamBarState) => {
    let isSharedInTeam = false;
    if (shareInTeamState === ShareInTeamBarState.ACTIVE) {
      isSharedInTeam = true;
    }

    setIsLoading(true);
    await shareEmailSignupFormInTeam(isSharedInTeam, signupForm.id);
    setIsLoading(false);
    setToggleState(shareInTeamState);
    onStateUpdate(shareInTeamState);

    if (gaEvent) {
      window.PMW.gtm.trackGA4CustomEvent(gaEvent, {
        [GA4EventParamName.TYPE]: isSharedInTeam ? CollaborationGA4Toggle.SHARED : CollaborationGA4Toggle.UNSHARED,
        [GA4EventParamName.SOURCE]: gaSource ?? CollaborationGA4Source.MY_STUFF,
      });
    }
  };

  if (isMobileWidth && !isInModal) {
    openShareSignupFormInTeamModal({signupForm, onStateUpdate});
    return <></>;
  }

  return (
    <div className={className}>
      <ShareInTeamToggleBar
        state={toggleState}
        isCurrentlyLoading={isLoading}
        onStateUpdate={(shareInTeamState: ShareInTeamBarState): void => {
          void updateSharingStatus(shareInTeamState);
        }}
      />
    </div>
  );
}
